import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Loader2, CheckCircle2, AlertCircle, FileText } from 'lucide-react';

/**
 * Extra Work editor for an appraisal — adds/edits/deletes rows captured
 * post-quote (on-site extras). Rendered inside JobTasksTab. Persists
 * via /api/appraisals/{id}/extra-work CRUD endpoints.
 *
 * Each row supports:
 *   – Date / Work Type / Materials + reference / Materials cost
 *   – Labour type (meterage / hourly / fixed) + quantity + rate
 *   – Comments
 *   – Client + Agent confirmation toggles (with "confirmed by" free text)
 *   – Invoice action: add | note | breakdown
 *   – Add-to-contractor-list flag
 *   – Optional override total
 */
export default function ExtraWorkEditor({ appraisalId }) {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [totals, setTotals] = useState({ invoice_total: 0, contractor_total: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/appraisals/${appraisalId}/extra-work`);
      setItems(data?.items || []);
      setTotals({
        invoice_total: data?.invoice_total || 0,
        contractor_total: data?.contractor_total || 0,
      });
    } catch (err) {
      setItems([]);
    }
    setLoading(false);
  }, [api, appraisalId]);

  useEffect(() => { load(); }, [load]);

  const addRow = async () => {
    setSaving('new');
    try {
      await api.post(`/appraisals/${appraisalId}/extra-work`, {
        appraisal_id: appraisalId,
        work_date: new Date().toISOString().slice(0, 10),
        work_type: '',
        invoice_action: 'add',
        add_to_contractor_list: true,
      });
      toast.success('Extra work row added');
      load();
    } catch (err) {
      toast.error('Failed to add row');
    }
    setSaving(null);
  };

  const patch = async (id, partial) => {
    // Optimistic update so the user feels the change instantly
    setItems(prev => prev.map(it => it._id === id ? { ...it, ...partial } : it));
    setSaving(id);
    try {
      const { data } = await api.patch(`/appraisals/${appraisalId}/extra-work/${id}`, partial);
      setItems(prev => prev.map(it => it._id === id ? data : it));
      // Refresh totals
      const { data: full } = await api.get(`/appraisals/${appraisalId}/extra-work`);
      setTotals({
        invoice_total: full?.invoice_total || 0,
        contractor_total: full?.contractor_total || 0,
      });
    } catch (err) {
      toast.error('Save failed — reload to re-fetch');
    }
    setSaving(null);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this extra-work row?')) return;
    setSaving(id);
    try {
      await api.delete(`/appraisals/${appraisalId}/extra-work/${id}`);
      toast.success('Row deleted');
      load();
    } catch (err) {
      toast.error('Delete failed');
    }
    setSaving(null);
  };

  return (
    <section data-testid="extra-work-editor" className="bg-white border border-[#E2E2DF] rounded-md p-4 space-y-3">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <FileText size={14} className="text-[#D35400]" /> Extra Work
          </h3>
          <p className="text-[11px] text-slate-500">On-site extras captured after the original quote — feeds into Page 2 of the Work Sheet PDF + invoice/contractor totals below.</p>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <span className="text-slate-600"><b>To Invoice:</b> <span className="text-emerald-700 font-mono-num">${totals.invoice_total.toLocaleString()}</span></span>
          <span className="text-slate-600"><b>To Contractor List:</b> <span className="text-amber-700 font-mono-num">${totals.contractor_total.toLocaleString()}</span></span>
          <button
            data-testid="extra-work-add-btn"
            onClick={addRow}
            disabled={saving === 'new'}
            className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md bg-[#D35400] text-white font-bold hover:bg-[#A04000] transition-colors disabled:opacity-40"
          >
            {saving === 'new' ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />} Add row
          </button>
        </div>
      </header>

      {loading ? (
        <div className="text-center py-4 text-xs text-slate-400 flex items-center justify-center gap-2">
          <Loader2 size={12} className="animate-spin" /> Loading extras…
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-6 text-xs text-slate-400 italic">
          No extra work captured yet. Click <b>Add row</b> when an on-site extra needs recording.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(it => (
            <ExtraWorkRow
              key={it._id}
              it={it}
              busy={saving === it._id}
              onPatch={(p) => patch(it._id, p)}
              onDelete={() => remove(it._id)}
            />
          ))}
        </div>
      )}
    </section>
  );
}


function ExtraWorkRow({ it, busy, onPatch, onDelete }) {
  // local state for free-text fields so we can debounce-blur-save without
  // hammering the API on every keystroke.
  const [local, setLocal] = useState(it);
  useEffect(() => { setLocal(it); }, [it]);
  const setL = (k, v) => setLocal(prev => ({ ...prev, [k]: v }));
  const blurSave = (k) => {
    if (local[k] !== it[k]) onPatch({ [k]: local[k] });
  };

  const computed = it.computed_total ?? 0;

  return (
    <div data-testid={`extra-work-row-${it._id}`} className="border border-slate-200 rounded-md bg-slate-50/50 p-3 space-y-2 text-[11px]">
      <div className="grid grid-cols-12 gap-2">
        <Field label="Date" cols={2}>
          <input type="date" value={(local.work_date || '').slice(0, 10)}
                 onChange={e => setL('work_date', e.target.value)} onBlur={() => blurSave('work_date')}
                 className="w-full px-1.5 py-1 text-[11px] border border-slate-300 rounded" />
        </Field>
        <Field label="Work Type" cols={4}>
          <input type="text" value={local.work_type || ''}
                 onChange={e => setL('work_type', e.target.value)} onBlur={() => blurSave('work_type')}
                 placeholder="e.g. Replace fascia 2.4m"
                 className="w-full px-1.5 py-1 text-[11px] border border-slate-300 rounded" />
        </Field>
        <Field label="Materials Required" cols={4}>
          <input type="text" value={local.materials_required || ''}
                 onChange={e => setL('materials_required', e.target.value)} onBlur={() => blurSave('materials_required')}
                 placeholder="Cedar fascia + galv nails"
                 className="w-full px-1.5 py-1 text-[11px] border border-slate-300 rounded" />
        </Field>
        <Field label="Mat. Ref" cols={2}>
          <input type="text" value={local.materials_ref || ''}
                 onChange={e => setL('materials_ref', e.target.value)} onBlur={() => blurSave('materials_ref')}
                 placeholder="INV-2031"
                 className="w-full px-1.5 py-1 text-[11px] border border-slate-300 rounded" />
        </Field>
      </div>
      {/* Materials qty / units row — feeds the printed "Extra Materials" sub-table */}
      <div className="grid grid-cols-12 gap-2">
        <Field label="Mat. Qty" cols={2}>
          <input type="number" step="0.01" value={local.materials_qty ?? 0}
                 onChange={e => setL('materials_qty', parseFloat(e.target.value) || 0)} onBlur={() => blurSave('materials_qty')}
                 className="w-full px-1.5 py-1 text-[11px] border border-slate-300 rounded font-mono-num" />
        </Field>
        <Field label="Mat. Units" cols={2}>
          <input type="text" value={local.materials_units || ''}
                 onChange={e => setL('materials_units', e.target.value)} onBlur={() => blurSave('materials_units')}
                 placeholder="lm / ea / m²"
                 className="w-full px-1.5 py-1 text-[11px] border border-slate-300 rounded" />
        </Field>
      </div>
      <div className="grid grid-cols-12 gap-2">
        <Field label="Mat. Cost" cols={2}>
          <input type="number" step="0.01" value={local.materials_cost ?? 0}
                 onChange={e => setL('materials_cost', parseFloat(e.target.value) || 0)} onBlur={() => blurSave('materials_cost')}
                 className="w-full px-1.5 py-1 text-[11px] border border-slate-300 rounded font-mono-num" />
        </Field>
        <Field label="Labour Type" cols={2}>
          <select value={local.labour_type || ''} onChange={e => { setL('labour_type', e.target.value); onPatch({ labour_type: e.target.value }); }}
                  className="w-full px-1.5 py-1 text-[11px] border border-slate-300 rounded">
            <option value="">—</option>
            <option value="meterage">Meterage</option>
            <option value="hourly">Hourly</option>
            <option value="fixed">Fixed</option>
          </select>
        </Field>
        <Field label="Qty" cols={1}>
          <input type="number" step="0.01" value={local.labour_quantity ?? 0}
                 onChange={e => setL('labour_quantity', parseFloat(e.target.value) || 0)} onBlur={() => blurSave('labour_quantity')}
                 className="w-full px-1.5 py-1 text-[11px] border border-slate-300 rounded font-mono-num" />
        </Field>
        <Field label="Rate ($)" cols={2}>
          <input type="number" step="0.01" value={local.labour_rate ?? 0}
                 onChange={e => setL('labour_rate', parseFloat(e.target.value) || 0)} onBlur={() => blurSave('labour_rate')}
                 className="w-full px-1.5 py-1 text-[11px] border border-slate-300 rounded font-mono-num" />
        </Field>
        <Field label="Override Total" cols={2}>
          <input type="number" step="0.01" value={local.override_total ?? ''}
                 placeholder={`auto $${computed.toLocaleString()}`}
                 onChange={e => setL('override_total', e.target.value === '' ? null : parseFloat(e.target.value))}
                 onBlur={() => blurSave('override_total')}
                 className="w-full px-1.5 py-1 text-[11px] border border-slate-300 rounded font-mono-num" />
        </Field>
        <Field label="Computed" cols={2}>
          <div className="px-1.5 py-1 text-[11px] font-bold text-emerald-700 font-mono-num">${computed.toLocaleString()}</div>
        </Field>
        <Field label="" cols={1}>
          <button data-testid={`extra-work-delete-${it._id}`} onClick={onDelete} disabled={busy}
                  className="p-1 rounded text-red-500 hover:bg-red-50">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
          </button>
        </Field>
      </div>
      <div className="grid grid-cols-12 gap-2">
        <Field label="Comments / Description" cols={6}>
          <input type="text" value={local.comments || ''}
                 onChange={e => setL('comments', e.target.value)} onBlur={() => blurSave('comments')}
                 placeholder="Rot found under existing fascia"
                 className="w-full px-1.5 py-1 text-[11px] border border-slate-300 rounded" />
        </Field>
        <Field label="Client Confirmed" cols={3}>
          <div className="flex items-center gap-1">
            <input type="checkbox" data-testid={`client-confirmed-${it._id}`}
                   checked={!!local.client_confirmed}
                   onChange={e => { setL('client_confirmed', e.target.checked); onPatch({ client_confirmed: e.target.checked }); }} />
            <input type="text" value={local.client_confirmed_by || ''}
                   onChange={e => setL('client_confirmed_by', e.target.value)} onBlur={() => blurSave('client_confirmed_by')}
                   placeholder="By…"
                   className="flex-1 px-1.5 py-1 text-[11px] border border-slate-300 rounded" />
          </div>
        </Field>
        <Field label="Agent Confirmed" cols={3}>
          <div className="flex items-center gap-1">
            <input type="checkbox" data-testid={`agent-confirmed-${it._id}`}
                   checked={!!local.agent_confirmed}
                   onChange={e => { setL('agent_confirmed', e.target.checked); onPatch({ agent_confirmed: e.target.checked }); }} />
            <input type="text" value={local.agent_confirmed_by || ''}
                   onChange={e => setL('agent_confirmed_by', e.target.value)} onBlur={() => blurSave('agent_confirmed_by')}
                   placeholder="By…"
                   className="flex-1 px-1.5 py-1 text-[11px] border border-slate-300 rounded" />
          </div>
        </Field>
      </div>
      {/* Notification block — mirrors the printed Work Sheet's Notification panel */}
      <div className="grid grid-cols-12 gap-2">
        <Field label="Office Notified" cols={3}>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid={`office-notified-${it._id}`}
                   checked={!!local.office_notified}
                   onChange={e => { setL('office_notified', e.target.checked); onPatch({ office_notified: e.target.checked }); }} />
            <span className="text-[11px] text-slate-600">{local.office_notified ? 'Yes' : 'No'}</span>
          </label>
        </Field>
        <Field label="Notification Date" cols={3}>
          <input type="date" value={local.notification_date || ''}
                 onChange={e => setL('notification_date', e.target.value)} onBlur={() => blurSave('notification_date')}
                 className="w-full px-1.5 py-1 text-[11px] border border-slate-300 rounded" />
        </Field>
        <Field label="Notification Type" cols={3}>
          <select data-testid={`notification-type-${it._id}`}
                  value={local.notification_type || ''}
                  onChange={e => { setL('notification_type', e.target.value); onPatch({ notification_type: e.target.value }); }}
                  className="w-full px-1.5 py-1 text-[11px] border border-slate-300 rounded">
            <option value="">—</option>
            <option value="phone">Phone</option>
            <option value="email">Email</option>
            <option value="text">Text</option>
            <option value="in-person">In Person</option>
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-12 gap-2">
        <Field label="Invoice Action" cols={4}>
          <select data-testid={`invoice-action-${it._id}`}
                  value={local.invoice_action || 'add'}
                  onChange={e => { setL('invoice_action', e.target.value); onPatch({ invoice_action: e.target.value }); }}
                  className="w-full px-1.5 py-1 text-[11px] border border-slate-300 rounded">
            <option value="add">Add to Invoice (rolled into total)</option>
            <option value="breakdown">Add with Breakdown (separate line)</option>
            <option value="note">Note Only (not charged)</option>
          </select>
        </Field>
        <Field label="Add to Contractor List" cols={4}>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid={`contractor-list-${it._id}`}
                   checked={!!local.add_to_contractor_list}
                   onChange={e => { setL('add_to_contractor_list', e.target.checked); onPatch({ add_to_contractor_list: e.target.checked }); }} />
            <span className="text-[11px] text-slate-600">{local.add_to_contractor_list ? 'Yes — labour cost flows to contractor work list' : 'No — track only, do not bill contractor'}</span>
          </label>
        </Field>
        <Field label="Status" cols={4}>
          <div className="flex items-center gap-2 text-[10px]">
            {local.client_confirmed
              ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={10} /> Client OK</span>
              : <span className="inline-flex items-center gap-1 text-amber-600"><AlertCircle size={10} /> Awaiting client</span>}
            {(local.invoice_action || 'add') === 'note' && <span className="text-blue-600">· Not charged</span>}
          </div>
        </Field>
      </div>
    </div>
  );
}


function Field({ label, cols, children }) {
  // Tailwind's JIT purger needs literal class names — using inline style
  // for the col span so any cols value (1..12) works.
  return (
    <div style={{ gridColumn: `span ${cols} / span ${cols}` }}>
      {label && <label className="block text-[9px] uppercase tracking-wider font-bold text-slate-400 mb-0.5">{label}</label>}
      {children}
    </div>
  );
}
